import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Award } from 'lucide-react';

/**
 * Component to display user's level and XP progression
 */
const LevelProgressCard = ({ level, xp, nextLevelXp, isDarkMode }) => {
  // Calculate progress percentage
  const progressPercent = useMemo(() => {
    if (!nextLevelXp || nextLevelXp === 0) return 0;
    return Math.min(100, Math.max(0, (xp / nextLevelXp) * 100));
  }, [xp, nextLevelXp]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
    >
      <div className="flex items-center mb-4">
        <div className={`w-12 h-12 rounded-full ${isDarkMode ? 'bg-indigo-900' : 'bg-indigo-100'} flex items-center justify-center mr-4`}>
          <Award size={24} className={isDarkMode ? 'text-indigo-300' : 'text-indigo-600'} />
        </div>
        <div>
          <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Seviye {level}
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {xp}/{nextLevelXp} XP
          </p>
        </div>
        <motion.div 
          className={`ml-auto text-5xl font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 120 }}
        >
          {level}
        </motion.div>
      </div>

      {/* XP Progress Bar */}
      <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden dark:bg-gray-700 mb-2">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progressPercent}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="absolute h-full bg-gradient-to-r from-indigo-500 to-blue-500"
        />
      </div>

      {/* Progress details */}
      <div className="flex justify-between items-center">
        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span className="font-medium">{xp}</span> XP Kazanıldı
        </div>
        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span className="font-medium">{nextLevelXp - xp}</span> XP Sonraki seviyeye
        </div>
      </div>
      
      {/* Milestone markers */}
      <div className="flex justify-between mt-2 px-1">
        <div className="flex flex-col items-center">
          <div className={`w-1 h-1 rounded-full ${progressPercent > 0 ? isDarkMode ? 'bg-blue-400' : 'bg-blue-600' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
          <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>0%</div>
        </div>
        <div className="flex flex-col items-center">
          <div className={`w-1 h-1 rounded-full ${progressPercent >= 25 ? isDarkMode ? 'bg-blue-400' : 'bg-blue-600' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
          <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>25%</div>
        </div>
        <div className="flex flex-col items-center">
          <div className={`w-1 h-1 rounded-full ${progressPercent >= 50 ? isDarkMode ? 'bg-blue-400' : 'bg-blue-600' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
          <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>50%</div>
        </div>
        <div className="flex flex-col items-center">
          <div className={`w-1 h-1 rounded-full ${progressPercent >= 75 ? isDarkMode ? 'bg-blue-400' : 'bg-blue-600' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
          <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>75%</div>
        </div>
        <div className="flex flex-col items-center">
          <div className={`w-1 h-1 rounded-full ${progressPercent >= 100 ? isDarkMode ? 'bg-blue-400' : 'bg-blue-600' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
          <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>100%</div>
        </div>
      </div>
    </motion.div>
  );
};

export default LevelProgressCard;